// Canva/Figma-style magnetic snapping. Pure geometry — no DOM. Given a moving
// rect, the other (static) rects, and the page size, returns the snap
// adjustment (dx, dy) plus the guide lines to draw. All coordinates are in
// canvas (unzoomed) space.
//
// Snap candidates per axis:
//   - page edges + page center
//   - each static rect's near edge, far edge, and center
// The moving rect's left/center/right (X) and top/center/bottom (Y) are tested
// against every candidate; the closest match within `threshold` wins.

const DEFAULT_THRESHOLD = 6;

function axisCandidates(statics, pageSize, axis) {
  const out = [];
  const pageMax = axis === 'x' ? pageSize.width : pageSize.height;
  out.push(0, pageMax / 2, pageMax); // page left/center/right (or top/mid/bottom)
  for (const s of statics) {
    const start = axis === 'x' ? s.x : s.y;
    const size = axis === 'x' ? s.width : s.height;
    out.push(start, start + size / 2, start + size);
  }
  return out;
}

// Moving rect's three snap points on an axis: near edge, center, far edge.
function movingPoints(rect, axis) {
  const start = axis === 'x' ? rect.x : rect.y;
  const size = axis === 'x' ? rect.width : rect.height;
  return [start, start + size / 2, start + size];
}

function bestSnap(rect, statics, pageSize, axis, threshold) {
  const candidates = axisCandidates(statics, pageSize, axis);
  const points = movingPoints(rect, axis);
  let best = null; // { delta, dist }
  for (const p of points) {
    for (const c of candidates) {
      const delta = c - p;
      const dist = Math.abs(delta);
      if (dist <= threshold && (!best || dist < best.dist)) {
        best = { delta, dist };
      }
    }
  }
  if (!best) return { delta: 0, lines: [] };
  // Collect every candidate line that an edge lands on after applying delta
  // (so multiple aligned guides can show at once).
  const lines = [];
  for (const c of candidates) {
    for (const p of points) {
      if (Math.abs((p + best.delta) - c) < 0.5 && !lines.includes(c)) lines.push(c);
    }
  }
  return { delta: best.delta, lines };
}

export function computeSnap(moving, statics = [], pageSize = { width: 0, height: 0 }, threshold = DEFAULT_THRESHOLD) {
  if (!moving) return { dx: 0, dy: 0, guidesV: [], guidesH: [], snappedX: false, snappedY: false };
  const x = bestSnap(moving, statics, pageSize, 'x', threshold);
  const y = bestSnap(moving, statics, pageSize, 'y', threshold);
  return {
    dx: x.delta,
    dy: y.delta,
    guidesV: x.lines,   // vertical guide line x-positions
    guidesH: y.lines,   // horizontal guide line y-positions
    snappedX: x.lines.length > 0,
    snappedY: y.lines.length > 0,
  };
}

export const SNAP_THRESHOLD = DEFAULT_THRESHOLD;
